import { CommonModule, Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  NgZone,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import {
  PessoaDataService,
  ServicoDataService,
} from '@nomoh-monorepo/data-access-shared-services';

import { removerEspacos } from '@nomoh-monorepo/shared-angular-util-tools';
import {
  formatarCNPJ,
  formatarCPF,
  stringApenasNumeros,
  validarCNPJ,
  validarCPF,
} from '@nomoh-monorepo/shared-util-tools';

import {
  EstadosConst,
  IPaisOption,
  IPessoa,
  ehPaisBrasil,
  listarPaises,
  normalizarCodigoPais,
  PAIS_BRASIL,
} from '@nomoh-monorepo/shared-types';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputMaskModule } from 'primeng/inputmask'; // mantido para máscara de CEP
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { AppConfig } from '../../auth/models/app-config.interface';
import { FormFieldComponent } from '../../form-field/form-field.component';
import { NMultiselectComponent } from '../../n-multiselect/n-multiselect.component';
import { PhoneInputComponent } from '../../phone-input/phone-input.component';

@Component({
  selector: 'lib-editar',
  imports: [
    CommonModule,
    FormFieldComponent,
    TranslateModule,
    ReactiveFormsModule,
    ButtonModule,
    PasswordModule,
    SelectModule,
    ConfirmDialogModule,
    InputMaskModule, // mantido para máscara de CEP
    InputTextModule,
    NMultiselectComponent,
    SkeletonModule,
    PhoneInputComponent,
  ],
  standalone: true,
  templateUrl: './editar.component.html',
  styleUrl: './editar.component.scss',
})
export class EditarComponentComponent implements OnInit, AfterViewInit {
  form!: FormGroup;
  isLoading = signal<boolean>(false);
  $carregandoDados = signal<boolean>(true);
  $emailSomenteLeitura = signal<boolean>(false);
  $exibirCpf = signal(false);
  $enderecoBrasil = signal(true);
  isMobileView = signal<boolean>(
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 768px)').matches
      : false
  );
  filePreviewUrl: string | undefined = undefined;
  readonly maxImagemBytes = 10 * 1024 * 1024;
  $erroImagem = signal<string | null>(null);
  estados = EstadosConst.sigla;
  paises: IPaisOption[] = listarPaises();
  sexoOpcoes = [
    { label: 'Não informado', value: 0 },
    { label: 'Masculino', value: 1 },
    { label: 'Feminino', value: 2 },
  ];
  idUsuario = signal<string>('');

  @ViewChild('fileInputImagem')
  private fileInputImagem?: ElementRef<HTMLInputElement>;

  // Injeção de dependências
  private fb = inject(FormBuilder);
  private pessoaDataService = inject(PessoaDataService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private notificacaoService = inject(NotificacaoService);
  private servicoDataService = inject(ServicoDataService);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  location = inject(Location);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private paisAnterior = '';
  private ignorarLimpezaEndereco = true;
  private appConfig: AppConfig | null = null;

  ngOnInit() {
    this.appConfig = this.route.snapshot.data['appConfig'] || null;
    this.criarFormulario();
  }

  ngAfterViewInit() {
    // Força detecção de mudanças após view inicializar
    this.cdr.detectChanges();

    // FIX: Restaura scroll do body (n-popover deixa overflow: hidden)
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';

    // Scroll para o topo
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  }

  criarFormulario() {
    this.form = this.fb.group({
      // Dados Pessoais
      nome: ['', [Validators.required, this.nomeSobrenomeValidator]],
      cpfCnpj: ['', [this.cpfCnpjValidator]],
      email: ['', [Validators.required, Validators.email]],
      telefone: ['', Validators.required],
      sexo: [0],
      // Upload de imagem
      imagemPerfil: [''],
      typeImage: [''],
      // Endereço
      endereco: this.fb.group({
        codPostal: ['', Validators.required],
        logradouro: ['', Validators.required],
        numero: ['', Validators.required],
        complemento: [''],
        bairro: ['', Validators.required],
        cidade: ['', Validators.required],
        estado: ['', Validators.required],
        pais: ['', Validators.required],
        latitude: [''],
        longitude: [''],
      }),
    });
    this.idUsuario.set(this.route.snapshot.paramMap.get('id') || '');

    const paisCtrl = this.form.get('endereco.pais')!;
    paisCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pais) => this.sincronizarPais(pais));
    this.sincronizarPais(paisCtrl.value);

    this.$carregandoDados.set(true);
    this.pessoaDataService
      .buscarPorId(this.idUsuario())
      .then((usuario) => {
        if (!usuario) {
          return;
        }

        this.form.patchValue(usuario);
        this.aplicarCpfFormatado(usuario.cpfCnpj);
        void this.aplicarPais(usuario.endereco?.pais);

        if (usuario.contaOAuth) {
          this.$emailSomenteLeitura.set(true);
          this.form.get('email')?.disable({ emitEvent: false });
        }

        if (usuario.imagemPerfil) {
          this.filePreviewUrl = `data:${
            usuario.typeImage || 'image/png'
          };base64,${usuario.imagemPerfil}`;
        }

        this.cdr.detectChanges();
      })
      .finally(() => {
        this.ignorarLimpezaEndereco = false;
        this.$carregandoDados.set(false);
        this.cdr.detectChanges();
      });
  }

  // Validações customizadas
  nomeSobrenomeValidator(control: any) {
    const nomeCompleto = control.value?.trim();
    if (!nomeCompleto || nomeCompleto.split(' ').length < 2) {
      return { nomeIncompleto: true };
    }
    return null;
  }

  get paisControl(): FormControl {
    return this.form.get('endereco.pais') as FormControl;
  }

  get dropdownAppendTo(): string | undefined {
    return this.isMobileView() ? undefined : 'body';
  }

  private sincronizarPais(pais: unknown): void {
    const codigo = normalizarCodigoPais(typeof pais === 'string' ? pais : null);
    const brasil = codigo === PAIS_BRASIL;
    this.$exibirCpf.set(brasil);
    this.$enderecoBrasil.set(brasil);

    const cpf = this.form.get('cpfCnpj');
    if (brasil) {
      cpf?.enable({ emitEvent: false });
    } else {
      cpf?.disable({ emitEvent: false });
    }

    this.aplicarRegrasEndereco(codigo, brasil);
  }

  private aplicarRegrasEndereco(codigo: string, brasil: boolean): void {
    const mudouTipo = ehPaisBrasil(this.paisAnterior) !== brasil;
    this.paisAnterior = codigo;

    const endereco = this.form.get('endereco') as FormGroup;
    const bairro = endereco.get('bairro');
    const complemento = endereco.get('complemento');
    const estado = endereco.get('estado');
    const codPostal = endereco.get('codPostal');

    if (brasil) {
      bairro?.setValidators([Validators.required, Validators.maxLength(100)]);
      estado?.setValidators([Validators.required]);
      codPostal?.setValidators([Validators.required, Validators.maxLength(20)]);
    } else {
      bairro?.clearValidators();
      estado?.setValidators([Validators.required, Validators.maxLength(100)]);
      codPostal?.setValidators([Validators.required, Validators.maxLength(20)]);
    }

    if (!this.ignorarLimpezaEndereco && mudouTipo) {
      if (brasil) {
        if (!this.estados.includes(String(estado?.value ?? ''))) {
          estado?.setValue('', { emitEvent: false });
        }
        const digitos = String(codPostal?.value ?? '').replace(/\D/g, '');
        if (digitos.length !== 8) {
          codPostal?.setValue('', { emitEvent: false });
        }
        if (bairro?.value == null) {
          bairro?.setValue('', { emitEvent: false });
        }
      } else {
        bairro?.setValue(null, { emitEvent: false });
        complemento?.setValue(null, { emitEvent: false });
        if (this.estados.includes(String(estado?.value ?? ''))) {
          estado?.setValue('', { emitEvent: false });
        }
        const digitos = String(codPostal?.value ?? '').replace(/\D/g, '');
        if (digitos.length === 8) {
          codPostal?.setValue('', { emitEvent: false });
        }
      }
    }

    bairro?.updateValueAndValidity({ emitEvent: false });
    complemento?.updateValueAndValidity({ emitEvent: false });
    estado?.updateValueAndValidity({ emitEvent: false });
    codPostal?.updateValueAndValidity({ emitEvent: false });
  }

  private aplicarCpfFormatado(cpfCnpj?: string | null): void {
    if (!cpfCnpj) {
      return;
    }
    const limpo = stringApenasNumeros(cpfCnpj);
    const formatado = limpo.length <= 11 ? formatarCPF(limpo) : formatarCNPJ(limpo);
    this.form.get('cpfCnpj')?.setValue(formatado);
  }

  private async aplicarPais(paisSalvo?: string | null): Promise<void> {
    const controle = this.form.get('endereco.pais');
    if (!controle) {
      return;
    }

    if (paisSalvo?.trim()) {
      controle.setValue(normalizarCodigoPais(paisSalvo));
      return;
    }

    const codigo = await this.servicoDataService.buscarPaisPorIp();
    if (controle.value) {
      return;
    }
    controle.setValue(codigo ?? PAIS_BRASIL);
  }

  cpfCnpjValidator(control: any) {
    const valor = control.value;
    if (!valor) return null;

    const limpo = stringApenasNumeros(valor);
    if (!validarCPF(limpo) && !validarCNPJ(limpo)) {
      return { cpfCnpjInvalido: true };
    }
    return null;
  }

  onTelefoneChange(value: string): void {
    const ctrl = this.form.get('telefone');
    ctrl?.setValue(value);
    ctrl?.markAsDirty();
    ctrl?.markAsTouched();
  }

  // Formatação de CPF/CNPJ
  formatarCpfCnpj(event: Event) {
    const input = event.target as HTMLInputElement;
    const valor = stringApenasNumeros(input.value);

    if (valor.length <= 11) {
      input.value = formatarCPF(valor);
    } else {
      input.value = formatarCNPJ(valor);
    }

    this.form.get('cpfCnpj')?.setValue(input.value);
  }

  // Busca de CEP
  async buscarCep(event: Event) {
    const input = event.target as HTMLInputElement;
    const cep = stringApenasNumeros(input.value);
    if (cep.length === 8) {
      this.servicoDataService.buscarCep(cep).then((data: any) => {
        if (data && !data.erro) {
          this.form.patchValue({
            endereco: {
              logradouro: data.logradouro || '',
              bairro: data.bairro || '',
              cidade: data.localidade || '',
              estado: data.uf || '',
              pais: PAIS_BRASIL,
            },
          });
        }
      });
    }
  }

  // Upload de imagem
  selecionarImagem(): void {
    this.fileInputImagem?.nativeElement.click();
  }

  onImagemSelecionada(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file) return;

    this.processarImagem(file);
  }

  private processarImagem(file: File): void {
    if (!this.isArquivoImagem(file)) {
      this.mostrarErroImagem(
        this.translate.instant('GLOBAL.ERRO_IMAGEM_TIPO')
      );
      return;
    }

    if (file.size > this.maxImagemBytes) {
      this.mostrarErroImagem(
        this.translate.instant('GLOBAL.ERRO_IMAGEM_TAMANHO')
      );
      return;
    }

    this.$erroImagem.set(null);

    const reader = new FileReader();
    reader.onload = () => {
      this.ngZone.run(() => {
        const base64String = reader.result as string;
        this.form.patchValue({
          imagemPerfil: base64String.split(',')[1],
          typeImage: file.type || this.inferirTipoImagem(file),
        });
        this.filePreviewUrl = base64String;
        this.cdr.detectChanges();
      });
    };

    reader.readAsDataURL(file);
  }

  private isArquivoImagem(file: File): boolean {
    if (file.type.startsWith('image/')) return true;

    const extensao = file.name.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp'].includes(
      extensao ?? ''
    );
  }

  private inferirTipoImagem(file: File): string {
    const extensao = file.name.split('.').pop()?.toLowerCase();
    const tipos: Record<string, string> = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      heic: 'image/heic',
      heif: 'image/heif',
      bmp: 'image/bmp',
    };

    return tipos[extensao ?? ''] ?? 'image/jpeg';
  }

  private mostrarErroImagem(mensagem: string): void {
    this.ngZone.run(() => {
      this.$erroImagem.set(mensagem);
      this.notificacaoService.notificacaoWarn(mensagem);
      this.cdr.detectChanges();
    });
  }

  removerImagem() {
    this.filePreviewUrl = undefined;
    this.$erroImagem.set(null);
    this.form.patchValue({
      imagemPerfil: '',
      typeImage: '',
    });
  }
  // Submissão do formulário
  salvar() {
    removerEspacos(this.form);
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.notificacaoService.notificacaoWarn(
        this.translate.instant('PAGINA_EDITAR.MENSAGEM_ERROS_FORMULARIO')
      );
      return;
    }

    this.isLoading.set(true);

    const usuario = this.montarUsuario();

    this.pessoaDataService
      .atualizarUsuario(usuario)
      .then(() => {
        this.pessoaDataService.buscarPessoaLogada().then(() => {
          this.location.back();
        });
      })
      .finally(() => {
        this.isLoading.set(false);
      });
  }

  private montarUsuario(): IPessoa {
    const formValue = this.form.getRawValue();
    const cpfCnpj = stringApenasNumeros(formValue.cpfCnpj || '');
    const brasil = ehPaisBrasil(formValue.endereco.pais);
    const codPostal = brasil
      ? stringApenasNumeros(formValue.endereco.codPostal)
      : String(formValue.endereco.codPostal ?? '').trim();

    return {
      id: this.idUsuario(),
      nome: formValue.nome,
      cpfCnpj: cpfCnpj || null,
      email: formValue.email,
      telefone: formValue.telefone || null,
      sexo: formValue.sexo ?? null,
      idioma: null,
      imagemPerfil: formValue.imagemPerfil || '',
      typeImage: formValue.typeImage || '',
      status: formValue.status || 'ativo',
      endereco: {
        logradouro: formValue.endereco.logradouro,
        numero: formValue.endereco.numero,
        bairro: brasil ? formValue.endereco.bairro : null,
        cidade: formValue.endereco.cidade,
        estado: formValue.endereco.estado,
        complemento: brasil ? formValue.endereco.complemento || '' : null,
        pais: normalizarCodigoPais(formValue.endereco.pais),
        codPostal,
        latitude: null,
        longitude: null,
      },
    };
  }

  cancelar() {
    this.location.back();
  }

  navegarHome(): void {
    this.router.navigateByUrl(this.appConfig?.dashboardRoute || '/');
  }

  get placeholderNomeCompleto() {
    return this.translate.instant('PAGINA_EDITAR.PLACEHOLDER_NOME_COMPLETO');
  }
  get placeholderEmail() {
    return this.translate.instant('PAGINA_EDITAR.PLACEHOLDER_EMAIL');
  }
  get placeholderLogradouro() {
    return this.translate.instant('PAGINA_EDITAR.PLACEHOLDER_LOGRADOURO');
  }
  get placeholderComplemento() {
    return this.translate.instant('PAGINA_EDITAR.PLACEHOLDER_COMPLEMENTO');
  }
  get placeholderBairro() {
    return this.translate.instant('PAGINA_EDITAR.PLACEHOLDER_BAIRRO');
  }
  get placeholderCidade() {
    return this.translate.instant('PAGINA_EDITAR.PLACEHOLDER_CIDADE');
  }
  get placeholderEstado() {
    return this.translate.instant('PAGINA_EDITAR.PLACEHOLDER_ESTADO');
  }
  get placeholderSenha() {
    return this.translate.instant('PAGINA_EDITAR.PLACEHOLDER_SENHA');
  }
  get placeholderConfirmarSenha() {
    return this.translate.instant('PAGINA_EDITAR.PLACEHOLDER_CONFIRMAR_SENHA');
  }

  get placeHolderBotaoImagem() {
    return this.translate.instant('PAGINA_EDITAR.PLACEHOLDER_BOTAO_IMAGEM');
  }
}
